// Find My Part Card - Product card renderer
class FindMyPartCard {
  constructor() {
    this.template = document.getElementById('find-my-part-card-template');
  }

  /**
   * Create a product card from API data
   * @param {Object} apiProduct - Product data from API
   * @returns {HTMLElement} Card element
   */
  createCard(apiProduct) {
    if (!this.template) {
      console.error('Card template not found');
      return null;
    }

    // Clone template
    const cardElement = this.template.content.cloneNode(true).querySelector('li');

    // Extract product ID from GID
    const productId = this.extractProductIdFromGid(apiProduct.shopify_id);
    cardElement.dataset.productId = productId;

    // Set product URL (will be updated when full product loads)
    const productUrl = productId ? `/products/${productId}` : '#';

    // Get data from API
    const title = apiProduct.product_title || apiProduct.items_title || 'Untitled Product';
    const partNumber = apiProduct.items_part_number || '';
    const brand = apiProduct.items_brand_label || apiProduct.items_brand || '';
    const category = apiProduct.items_category || '';
    const partType = apiProduct.items_part_type || '';
    const isActive = apiProduct.product_status === 'ACTIVE';

    // Default placeholder image
    const placeholderImage = 'https://cdn.shopify.com/s/files/1/0748/9323/0294/files/GSP_Product.png?v=1768724379';

    // Update card status
    const card = cardElement.querySelector('.card');
    if (!isActive) {
      card.classList.remove('card--standard');
      card.classList.add('card--sold-out');
    }

    // Update links
    const links = cardElement.querySelectorAll('.product-link');
    links.forEach((link) => (link.href = productUrl));

    // Update title - show Part # instead of product title
    const titleElement = cardElement.querySelector('.product-title');
    if (titleElement) {
      titleElement.textContent = partNumber ? `Part #: ${partNumber}` : title;
    }

    // Update image with placeholder
    const primaryImg = cardElement.querySelector('.product-image-primary');
    if (primaryImg) {
      primaryImg.src = placeholderImage;
      primaryImg.alt = title;
      primaryImg.style.opacity = '1';
    }

    // Remove loading state
    const mediaContainer = cardElement.querySelector('.media');
    if (mediaContainer) {
      mediaContainer.classList.remove('media--loading');
    }

    // Update brand
    if (brand) {
      const brandElement = cardElement.querySelector('.card__brand');
      if (brandElement) {
        brandElement.textContent = brand;
        brandElement.style.display = 'block';
      }
    }


    // Update mfr_label badge
    const mfrLabelContainer = cardElement.querySelector('.card__mfr-label');
    if (mfrLabelContainer && apiProduct.mfr_label) {
      const mfrLabelBadge = mfrLabelContainer.querySelector('.mfr-label-badge');
      if (mfrLabelBadge) {
        mfrLabelBadge.textContent = apiProduct.mfr_label;
        mfrLabelContainer.style.display = 'block';
      }
    }

    // Update additional product details
    this.updateDetailField(cardElement, 'category', apiProduct.items_category);
    this.updateDetailField(cardElement, 'sub_category', apiProduct.items_sub_category);
    this.updateDetailField(cardElement, 'product_type', apiProduct.items_part_type);
    this.updateDetailField(cardElement, 'drive', apiProduct.drive);
    this.updateDetailField(cardElement, 'qualifier_text', apiProduct.qualifier);
    this.updateDetailField(cardElement, 'notes', apiProduct.notes);
    this.updateDetailField(cardElement, 'submodel', apiProduct.sub_model);
    this.updateDetailField(cardElement, 'engine_liter', apiProduct.engine_liter);
    this.updateDetailField(cardElement, 'position', apiProduct.position);
    this.updateDetailField(cardElement, 'transmission', apiProduct.transmission_type);
    this.updateDetailField(cardElement, 'fuel', apiProduct.fuel_type);

    // Set initial price state to "Request a quote" (will be updated with real price if available)
    const priceContainer = cardElement.querySelector('.card__price .price');
    if (priceContainer) {
      priceContainer.innerHTML = `<span class="price-item price-item--regular">Request a quote</span>`;
    }

    return cardElement;
  }

  /**
   * Update a detail field in the card
   * @param {HTMLElement} cardElement - Card element
   * @param {String} fieldName - Field name (data attribute value)
   * @param {String} value - Value to display
   */
  updateDetailField(cardElement, fieldName, value) {
    if (value) {
      const detailItem = cardElement.querySelector(`.detail-item[data-field="${fieldName}"]`);
      if (detailItem) {
        const valueElement = detailItem.querySelector('.detail-value');
        if (valueElement) {
          valueElement.textContent = value;
          detailItem.style.display = 'block';
        }
      }
    }
  }

  /**
   * Update card with pre-fetched Shopify product data from GraphQL
   * @param {HTMLElement} cardElement - Card element to update
   * @param {Object} product - Shopify product data from GraphQL
   */
  updateCardWithShopifyData(cardElement, product) {
    console.log('[Card] Updating card with product data:', product);

    if (!product) {
      console.error('[Card] No product data provided');
      return;
    }

    // Default placeholder image
    const placeholderImage = 'https://cdn.shopify.com/s/files/1/0748/9323/0294/files/GSP_Product.png?v=1768724379';

    // Update primary image
    const primaryImg = cardElement.querySelector('.product-image-primary');
    const secondaryImg = cardElement.querySelector('.product-image-secondary');
    const mediaContainer = cardElement.querySelector('.media');

    if (primaryImg) {
      // Use featured image or placeholder
      primaryImg.src = product.featuredImage || placeholderImage;
      primaryImg.alt = product.featuredImageAlt || product.title;
      primaryImg.style.opacity = '1';
      if (mediaContainer) {
        mediaContainer.classList.remove('media--loading');
      }
    }

    // Update secondary image (for hover effect)
    if (secondaryImg && product.secondImage) {
      secondaryImg.src = product.secondImage;
      secondaryImg.alt = product.secondImageAlt || product.title;
      secondaryImg.dataset.loaded = 'true';
    }

    // Update links with proper URL
    const productUrl = product.url;
    const links = cardElement.querySelectorAll('.product-link');
    links.forEach((link) => (link.href = productUrl));

    // Update price
    const priceContainer = cardElement.querySelector('.card__price .price');
    if (priceContainer) {
      const price = parseFloat(product.price);

      // Show "Request a quote" for products with no price or price = 0
      if (!product.price || price === 0) {
        priceContainer.innerHTML = `<span class="price-item price-item--regular">Request a quote</span>`;
      } else {
        const hasComparePrice = product.compareAtPrice && parseFloat(product.compareAtPrice) > price;

        if (hasComparePrice) {
          priceContainer.innerHTML = `
            <dl>
              <div class="price__regular">
                <dt><span class="visually-hidden">Regular price</span></dt>
                <dd><s class="price-item price-item--regular">${product.compareAtPriceFormatted}</s></dd>
              </div>
              <div class="price__sale">
                <dt><span class="visually-hidden">Sale price</span></dt>
                <dd><span class="price-item price-item--sale">${product.priceFormatted}</span></dd>
              </div>
            </dl>
          `;
          priceContainer.classList.add('price--on-sale');
        } else {
          priceContainer.innerHTML = `<span class="price-item price-item--regular">${product.priceFormatted}</span>`;
        }
      }
    }

    // Update availability status
    if (!product.available) {
      const card = cardElement.querySelector('.card');
      if (card) {
        card.classList.remove('card--standard');
        card.classList.add('card--sold-out');
      }
    }

    // Update Shopify tags
    if (product.tags && product.tags.length > 0) {
      const tagsContainer = cardElement.querySelector('.card__tags');
      if (tagsContainer) {
        // Clear existing tags
        tagsContainer.innerHTML = '';

        // Add each tag
        product.tags.forEach(tag => {
          const tagElement = document.createElement('span');
          tagElement.className = 'tag';
          tagElement.textContent = tag;
          tagElement.style.display = 'inline-block';
          tagElement.style.padding = '0.3rem 0.8rem';
          tagElement.style.fontSize = '1.1rem';
          tagElement.style.background = 'rgba(var(--color-foreground), 0.08)';
          tagElement.style.borderRadius = '4px';
          tagElement.style.fontWeight = '500';
          tagsContainer.appendChild(tagElement);
        });

        tagsContainer.style.display = 'flex';
      }
    }
  }

  /**
   * Extract product ID from Shopify GID
   * @param {String} gid - Shopify GID (e.g., gid://shopify/Product/123)
   * @returns {String} Product ID
   */
  extractProductIdFromGid(gid) {
    if (!gid) return null;
    const parts = gid.split('/');
    return parts[parts.length - 1];
  }
}

// Make available globally
window.FindMyPartCard = FindMyPartCard;
